<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { RouterView, RouterLink } from 'vue-router'
import { usePanelSize } from '@/composables/usePanelSize'

const appContainer = ref<HTMLElement | null>(null)

onMounted(() => {
  if (appContainer.value) {
    usePanelSize(appContainer.value)
  }
})
</script>

<template>
  <div class="app-container" ref="appContainer">
    <header class="app-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="app-title">Paraclete</h1>
          <span class="app-subtitle">Clinical Decision Support</span>
        </div>
        <nav class="header-nav">
          <RouterLink to="/" class="header-link">Inbox</RouterLink>
          <RouterLink to="/results" class="header-link">Results</RouterLink>
          <RouterLink to="/deployment" class="header-link">Deployment</RouterLink>
          <RouterLink to="/help" class="header-link">? Help</RouterLink>
        </nav>
      </div>
    </header>
    <main class="app-main">
      <RouterView />
    </main>
    <footer class="app-footer">
      <p>SMART on FHIR Clinical Decision Support System</p>
    </footer>
  </div>
</template>

<style>
:root {
  --primary-color: #1a5276;
  --primary-hover: #123d5c;
  --success-color: #27ae60;
  --warning-color: #f39c12;
  --danger-color: #c0392b;
  --info-color: #2980b9;
  --text-color: #2c3e50;
  --text-muted: #7f8c8d;
  --bg-color: #ecf0f1;
  --card-bg: #ffffff;
  --border-color: #d5d8dc;
  --shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-lg: 0 2px 6px rgba(0, 0, 0, 0.1);
  --primary-action: #2471a3;
  --border-radius: 4px;
  --header-bg: #1a3c5e;
  --font-size-base: 13px;
  --font-size-sm: 12px;
  --font-size-xs: 11px;
}

body {
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
  font-size: var(--font-size-base);
  line-height: 1.5;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background: var(--header-bg);
  color: white;
  padding: 0.375rem 0.75rem;
  box-shadow: none;
  border-bottom: 1px solid rgba(0,0,0,0.2);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: baseline;
  gap: 1rem;
}

.header-nav {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-link {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 0.85rem;
  opacity: 0.8;
  transition: opacity 0.15s ease;
}

.header-link:hover {
  color: white;
  opacity: 1.0;
}

.app-title {
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
}

.app-subtitle {
  font-size: 0.75rem;
  opacity: 0.8;
}

.app-main {
  flex: 1;
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.app-footer {
  background: var(--card-bg);
  border-top: 1px solid var(--border-color);
  padding: 0.25rem 0.75rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.6875rem;
}

/* Global utility classes */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.625rem;
  border-radius: 3px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color 0.15s ease;
  gap: 0.5rem;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background: var(--primary-hover);
}

.btn-success {
  background: var(--success-color);
  color: white;
}

.btn-success:hover {
  background: #1e8449;
}

.btn-danger {
  background: var(--danger-color);
  color: white;
}

.btn-danger:hover {
  background: #a93226;
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-color);
}

.btn-outline:hover {
  background: var(--bg-color);
}

.card {
  background: var(--card-bg);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: var(--text-muted);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-message {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  color: var(--danger-color);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin: 1rem 0;
}

/* Embed mode — hide chrome when running inside Epic iframe */
html.embed-mode .app-header { display: none; }
html.embed-mode .app-footer { display: none; }
html.embed-mode .app-main {
  padding: 0.75rem;
  max-width: none;
}
html.embed-mode .app-container { min-height: auto; }
html.embed-mode,
html.embed-mode body {
  overflow: auto;
  height: 100%;
}

/* Panel size responsive rules */
[data-panel-size="compact"] .message-preview { display: none; }
[data-panel-size="compact"] .stats-bar { flex-direction: column; gap: 0.25rem; }
</style>
